import { useCallback, useState } from 'react'

type BaseEmployee = {
  id: string
  name?: string
  is_pinned?: boolean
}

export const usePinnedEmployees = <T extends BaseEmployee>() => {
  const [pinnedEmployeeIds, setPinnedEmployeeIds] = useState<string[]>([])

  const handleEmployeePinToggle = useCallback((employeeId: string) => {
    setPinnedEmployeeIds(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId)
      } else {
        return [...prev, employeeId]
      }
    })
  }, [])

  const isEmployeePinned = useCallback((employeeId: string) => {
    return pinnedEmployeeIds.includes(employeeId)
  }, [pinnedEmployeeIds])

  const pinnedSortFunction = useCallback((
    rowA: { original: T },
    rowB: { original: T },
    columnId: string,
    getSortingDirection: (columnId: string) => 'asc' | 'desc' | false
  ): number | undefined => {
    const sortingDir = getSortingDirection(columnId)
    const aIsPinned = isEmployeePinned(rowA.original.id) || rowA.original.is_pinned
    const bIsPinned = isEmployeePinned(rowB.original.id) || rowB.original.is_pinned

    if (aIsPinned && !bIsPinned) return sortingDir === 'asc' ? -1 : 1
    if (!aIsPinned && bIsPinned) return sortingDir === 'asc' ? 1 : -1
    if (aIsPinned && bIsPinned) return 0
    return undefined
  }, [isEmployeePinned])

  const filterAndSortEmployees = useCallback((
    employees: T[],
    searchQuery: string = '',
    additionalFilters?: (employee: T) => boolean
  ): T[] => {
    const pinnedEmployees: T[] = []
    const filteredEmployees: T[] = []
    const normalizedSearchQuery = searchQuery.trim().toLowerCase()

    employees.forEach((employee) => {
      if (isEmployeePinned(employee.id)) {
        pinnedEmployees.push(employee)
      } else {
        const matchesSearch = !searchQuery ||
          employee.name?.toLowerCase().includes(normalizedSearchQuery)

        const matchesAdditionalFilters = !additionalFilters || additionalFilters(employee)

        if (matchesSearch && matchesAdditionalFilters) {
          filteredEmployees.push(employee)
        }
      }
    })

    return [...pinnedEmployees, ...filteredEmployees]
  }, [isEmployeePinned])

  const createPinnedSortingFn = useCallback((
    getSortingDirection: (columnId: string) => 'asc' | 'desc' | false
  ) => {
    return (rowA: { original: T }, rowB: { original: T }, columnId: string): number => {
      const pinnedResult = pinnedSortFunction(rowA, rowB, columnId, getSortingDirection)
      if (pinnedResult !== undefined) return pinnedResult

      // If no pinned sorting applies, return 0 to let the default sorting handle it
      return 0
    }
  }, [pinnedSortFunction])

  return {
    pinnedEmployeeIds,
    setPinnedEmployeeIds,
    handleEmployeePinToggle,
    isEmployeePinned,
    pinnedSortFunction,
    filterAndSortEmployees,
    createPinnedSortingFn
  }
}
